import React, { useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export default function SendEmails({ selectedTransactions = [] }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [template, setTemplate] = useState("");
  const [filters, setFilters] = useState({ kycStatus: "", country: "", role: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const templates = {
    welcome: {
      subject: "🎉 Bienvenue chez NGC Financial",
      message: `
        <h2 style="color:#2563eb">Bienvenue !</h2>
        <p>Bonjour {{userName}},</p>
        <p>Merci d'avoir rejoint NGC. Activez vite votre compte pour profiter de nos services.</p>
        <img src="https://placehold.co/600x200" alt="Bienvenue" style="max-width:100%;"/>
      `,
    },
    transactionUpdate: {
      subject: "Mise à jour de votre transaction",
      message: `
        <p>Bonjour {{userName}},</p>
        <p>Votre transaction <strong>#{{transactionId}}</strong> d’un montant de <strong>{{amount}} {{currency}}</strong> est maintenant <strong>{{status}}</strong>.</p>
        <p>Merci d’utiliser NGC Financial 🙏</p>
      `,
    },
    promo: {
      subject: "🔥 Offre exclusive pour vous",
      message: `
        <h3>Ne ratez pas notre nouvelle promo</h3>
        <p>Bénéficiez de <strong>0% de frais</strong> sur vos prochains transferts jusqu'au <em>15 septembre</em>.</p>
        <a href="https://ngc-financial.com/offres" 
           style="background:#2563eb;color:#fff;padding:10px 20px;border-radius:6px;text-decoration:none;">
           Profiter de l’offre
        </a>
      `,
    },
  };

  // Lorsqu'on choisit un template
  const handleTemplateSelect = (value) => {
    setTemplate(value);
    if (templates[value]) {
      setSubject(templates[value].subject);
      setMessage(templates[value].message);
    }
  };

  // Envoi au backend
  const handleSend = async () => {
    setSending(true);
    setResult(null);

    try {
      const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
      const url = `${base}/api/admin/emails/send-emails`;

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
        subject,
        message,
        filters,
        mode: selectedTransactions?.length ? "transactionIds" : "filtered",
        transactionIds: selectedTransactions.map((t) => t.id),
        throttleMs: 250
      }),
      });

      const data = await res.json();
      if (res.ok) {
        setResult({ success: true, message: data.message || "Emails envoyés avec succès." });
      } else {
        setResult({ success: false, message: data.message || "Erreur serveur" });
      }
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow max-w-3xl mx-auto">
      <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
        Envoyer un email {selectedTransactions.length > 0 && `(${selectedTransactions.length} transaction(s))`}
      </h2>

      {/* Template Selector */}
      <div className="mb-4">
        <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-300">
          Modèle prédéfini
        </label>
        <select
          value={template}
          onChange={(e) => handleTemplateSelect(e.target.value)}
          className="w-full border px-3 py-2 rounded dark:bg-gray-700 dark:text-white"
        >
          <option value="">Aucun</option>
          <option value="welcome">Bienvenue</option>
          <option value="transactionUpdate">Mise à jour transaction</option>
          <option value="promo">Promotion</option>
        </select>
      </div>

      {/* Subject */}
      <div className="mb-4">
        <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-300">Objet</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border px-3 py-2 rounded dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* Message (éditeur HTML) */}
      <div className="mb-4">
        <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-300">Message</label>
        <ReactQuill value={message} onChange={setMessage} theme="snow" className="bg-white dark:bg-gray-700 dark:text-white" />
      </div>

      {/* Filtres avancés */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-300">Statut KYC</label>
          <select
            value={filters.kycStatus}
            onChange={(e) => setFilters((prev) => ({ ...prev, kycStatus: e.target.value }))}
            className="w-full border px-3 py-2 rounded dark:bg-gray-700 dark:text-white"
          >
            <option value="">Tous</option>
            <option value="Approved">Approuvé</option>
            <option value="Pending">En attente</option>
            <option value="Rejected">Rejeté</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-300">Pays</label>
          <input
            type="text"
            value={filters.country}
            onChange={(e) => setFilters((prev) => ({ ...prev, country: e.target.value }))}
            className="w-full border px-3 py-2 rounded dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label className="block font-semibold mb-1 text-gray-700 dark:text-gray-300">Rôle utilisateur</label>
          <select
            value={filters.role}
            onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
            className="w-full border px-3 py-2 rounded dark:bg-gray-700 dark:text-white"
          >
            <option value="">Tous</option>
            <option value="client">Client</option>
            <option value="admin">Admin</option>
            <option value="superadmin">Superadmin</option>
          </select>
        </div>
      </div>

      {/* Send button */}
      <button
        onClick={handleSend}
        disabled={sending}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {sending ? "Envoi..." : "Envoyer"}
      </button>

      {/* Résultat */}
      {result && (
        <div
          className={`mt-4 p-3 rounded ${
            result.success ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          <p>{result.message}</p>
        </div>
      )}

      {/* Preview */}
      <div className="mt-6 border-t pt-4">
        <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Prévisualisation</h3>
        <div
          className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:text-gray-200"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </div>
    </div>
  );
}
